'use client'

import { useAuth } from '@/providers/auth-provider'
import { useImpersonation } from '@/plugins/impersonate/components'
import { isTwoFactorPending } from '../helpers/twoFactor'

/**
 * Client mirror of the server's `isTwoFactorBlocked` (`@/utils/authUtils`): a 2FA-pending
 * limited session (#3459) is denied tenant data — *unless* the session is an impersonation.
 *
 * #3891 exempted impersonation on the server, but the shell kept its own copy of the gate
 * (#3566) and went on skipping every tenant-data fetch. Impersonating a 2FA-pending target
 * therefore still rendered an empty app: `CompaniesProvider` never loaded companies, and the
 * meetings table keys its `/api/meetings/future-meetings` request off the selected company ids
 * — with none selected the request URL is `null` and no fetch is ever made. The server had
 * already stopped refusing; nobody was asking. A target *without* 2FA was unaffected, which is
 * why impersonating one user showed meetings and another showed none.
 *
 * `isImpersonating` starts `false` and flips once `/impersonate/status` resolves, so callers
 * must include the returned flag in their effect deps — the fetch fires on that second pass.
 *
 * This is a UX gate only. Nothing here grants access: the exemption that matters is the
 * server's, keyed on the HMAC-signed impersonation cookie, and it re-checks every request.
 *
 * `isImpersonating` here is NOT the same predicate as the server's: `/impersonate/status` reports
 * only that the impersonation cookies are *present*, while the server's exemption additionally
 * requires a valid HMAC over `impersonator:target`. So the client can believe it is impersonating
 * when the server disagrees — the fetch then fires and returns 403 (the console noise #3566
 * removed). The failure mode is one-directional and benign: a client false *positive* costs a
 * rejected request, never access. Do not treat the two flags as equivalent.
 *
 * Requires an `ImpersonationProvider` ancestor; the frontend layout mounts one above every
 * consumer of this hook.
 */
export const useTwoFactorBlocked = (): boolean => {
  const { user } = useAuth()
  const { isImpersonating } = useImpersonation()
  return isTwoFactorPending(user) && !isImpersonating
}
